import { useCallback, useState } from 'react';
import {
  addMonths,
  eachMonthOfInterval,
  format,
  isSameMonth,
  startOfMonth,
  startOfYear,
} from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusiness } from '../contexts/BusinessContext';

export type ConversionByMonthRow = {
  monthKey: string;
  label: string;
  rate: number;
  won: number;
  created: number;
  isCurrentMonth: boolean;
};

function monthKeyInTimezone(iso: string, timezone: string) {
  return format(toZonedTime(new Date(iso), timezone), 'yyyy-MM');
}

/**
 * Conversion rate per month — of leads *created* in a month, the % currently
 * Won — January through the current month (business timezone). Grouped by
 * `created_at` (the lead cohort), matching the "Conversion rate" KPI.
 *
 * Note: recent months read low because newly-created leads may still be open.
 */
export function useConversionByMonthYtdBreakdown() {
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const timezone =
    selectedBusiness?.timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone ||
    'UTC';

  const [months, setMonths] = useState<ConversionByMonthRow[]>([]);
  const [yearRate, setYearRate] = useState(0);
  const [yearWon, setYearWon] = useState(0);
  const [yearCreated, setYearCreated] = useState(0);
  const [yearLabel, setYearLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!supabase || !businessId) {
      setMonths([]);
      setYearRate(0);
      setYearWon(0);
      setYearCreated(0);
      setYearLabel('');
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const zonedNow = toZonedTime(new Date(), timezone);
      const yearStartZoned = startOfYear(zonedNow);
      const currentMonthZoned = startOfMonth(zonedNow);

      setYearLabel(format(zonedNow, 'yyyy'));

      const rangeStart = fromZonedTime(yearStartZoned, timezone).toISOString();
      const rangeEnd = fromZonedTime(
        startOfMonth(addMonths(zonedNow, 1)),
        timezone
      ).toISOString();

      const { data, error: queryError } = await supabase
        .from('pipeline_opportunities')
        .select('status, created_at')
        .eq('business_id', businessId)
        .not('created_at', 'is', null)
        .gte('created_at', rangeStart)
        .lt('created_at', rangeEnd);

      if (queryError) throw queryError;

      const createdByMonth = new Map<string, number>();
      const wonByMonth = new Map<string, number>();
      let totalCreated = 0;
      let totalWon = 0;

      for (const opp of data ?? []) {
        if (!opp.created_at) continue;
        const key = monthKeyInTimezone(opp.created_at, timezone);
        createdByMonth.set(key, (createdByMonth.get(key) ?? 0) + 1);
        totalCreated += 1;
        if (opp.status === 'Won') {
          wonByMonth.set(key, (wonByMonth.get(key) ?? 0) + 1);
          totalWon += 1;
        }
      }

      const monthDates = eachMonthOfInterval({
        start: yearStartZoned,
        end: currentMonthZoned,
      });

      const rows: ConversionByMonthRow[] = monthDates.map((month) => {
        const key = format(month, 'yyyy-MM');
        const created = createdByMonth.get(key) ?? 0;
        const won = wonByMonth.get(key) ?? 0;
        return {
          monthKey: key,
          label: format(month, 'MMMM yyyy'),
          rate: created > 0 ? (won / created) * 100 : 0,
          won,
          created,
          isCurrentMonth: isSameMonth(month, zonedNow),
        };
      });

      setMonths(rows);
      setYearWon(totalWon);
      setYearCreated(totalCreated);
      setYearRate(totalCreated > 0 ? (totalWon / totalCreated) * 100 : 0);
    } catch (err) {
      console.error('Error loading conversion by month YTD breakdown:', err);
      setMonths([]);
      setYearRate(0);
      setYearWon(0);
      setYearCreated(0);
      setError('Could not load conversion breakdown.');
    } finally {
      setLoading(false);
    }
  }, [supabase, businessId, timezone]);

  return { months, yearRate, yearWon, yearCreated, yearLabel, loading, error, load };
}
